// library/view — the Library (Bibliotek): a searchable TABLE you walk INTO.
// Owner-approved designs meet here:
//
//   - LIBRARY SEARCH (2026-10-01): the query TEXT is the single source of
//     truth (libraryQuery.ts) — the kind tabs write `type:`, the text selects
//     the tab. Free text groups one row per resource under its best field
//     (title, author), then the content hits.
//   - LIBRARY TABLE (D23, 2026-10-06): a conventional table under the search
//     field, a header row naming the columns; a header click sorts by it.
//   - LIBRARY DRILL-IN: ONE click (or Enter) goes INTO the row: the item
//     takes the view over it as a sheet with a quiet
//     prev/next and a close x through the rows as shown. The table stays MOUNTED (hidden)
//     underneath, so query, tab, sort, scroll and the marked row survive the
//     way back (the x or browser Back).
//
// A PROJECTION + a command mapping, no third path: the query text and the
// opened row are frame state (projected in), the marked row and the sort are
// the module's Model; every change is a module Msg (`send`). Local state is
// view-ephemeral only: an un-submitted rename. `renderItem` is the host's
// item view (fed by the module's item read).

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
    type KeyboardEvent,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import { formatCount } from "../../app/displayLimits.js";
import type { SmartFilter } from "../../app/ports/workspace.js";
import { Button } from "../../components/Button/Button.js";
import { EmptyState } from "../../components/EmptyState/EmptyState.js";
import { Icon } from "../../components/Icon/Icon.js";
import { Menu, type MenuItem } from "../../components/Menu/Menu.js";
import { pageFocusTarget } from "../../components/Navigation/focusTarget.js";
import {
    isLeaveKey,
    leaveKeyOf,
} from "../../components/Navigation/studioReturn.js";
import { RowOverflow } from "../../components/RowOverflow/RowOverflow.js";
import { RowRename } from "../../components/RowOverflow/RowRename.js";
import type { ContentSection } from "../../components/Search/contentSearch.js";
import { DrillInBar } from "../../components/Search/DrillInBar.js";
import {
    type ModifierKey,
    withScope,
} from "../../components/Search/libraryQuery.js";
import {
    intentOfAction,
    type LibraryRowIntent,
    refOfResult,
    rowActionsFor,
} from "../../components/Search/libraryRowIntent.js";
import {
    type ListKey,
    QueryField,
} from "../../components/Search/QueryField.js";
import type {
    LibrarySort,
    SearchResult,
    SearchResultKind,
} from "../../components/Search/searchModel.js";
import { Text } from "../../components/Text/Text.js";
import {
    ViewColumn,
    ViewHeader,
} from "../../components/ViewHeader/ViewHeader.js";
import { CreateCard } from "./CreateCard.js";
import { LibraryTable, type RowKey } from "./LibraryTable.js";
import { useLibraryLabels } from "./labels.js";
import type { LibraryModel } from "./model.js";
import type { LibraryMsg } from "./msg.js";
import { SmartFilterBar } from "./SmartFilterBar.js";
import { projectLibrary, projectSmartFilters } from "./view-model.js";
import "./library.css";

export type LibraryViewProps = {
    readonly model: LibraryModel;
    readonly send: (msg: LibraryMsg) => void;
    // The resources this surface lists (the host's projection of the live
    // Library, its visible kinds only) and those kinds.
    readonly pool: readonly SearchResult[];
    readonly visibleKinds: readonly SearchResultKind[];
    // The frame's query text and opened row.
    readonly query: string;
    readonly openedId: string | null;
    // The opened item's view (the host's, fed by the module's item read).
    readonly renderItem: (result: SearchResult) => ReactNode;
    // A row's lifecycle intent; absent ⇒ rows carry no lifecycle actions.
    readonly onRowIntent?: (intent: LibraryRowIntent) => void;
    // The Author's saved searches (the host's projection of the Library).
    readonly smartFilters?: readonly SmartFilter[];
    // Open the file picker to add sources (FR-3.1, FR-8.3); absent ⇒ no
    // paperclip.
    readonly onAddSource?: () => void;
    // Hand a "make this" request to the assistant (a new conversation in the
    // chat panel).
    readonly onCreate: (request: string) => void;
    // The files still uploading (the host's ingest batch): no source exists
    // for them yet, so they show as a line above the table (FR-3.3).
    readonly uploading?: readonly string[];
    // Whether the pool is the workspace's (loaded) or nothing has loaded yet
    // because the server can't be reached (waiting): then the count, tabs and
    // table give way to a note instead of claiming the library is empty.
    readonly poolState?: LibraryPoolState;
    // The opened item was entered from elsewhere (the Studio): its bar leads
    // back there, and so does Esc.
    readonly returnTo?: {
        readonly label: string;
        readonly onReturn: () => void;
    };
};

type LibraryPoolState = "loaded" | "waiting";

const rowId = (id: string): string => `bsx-row-${id}`;

// The filters shown as chips under the field (a tab's own token is the tab).
const CHIP_KEYS: readonly ModifierKey[] = ["author", "created"];

// The sort menu's choices (the design's "Senast ändrat ▾"): each sets exactly
// its sort.
type SortPreset = "edited" | "title" | "kind";
const SORT_PRESETS: Record<SortPreset, LibrarySort> = {
    edited: { column: "edited", direction: "descending" },
    title: { column: "title", direction: "ascending" },
    kind: { column: "kind", direction: "ascending" },
};
const SORT_PRESET_ORDER: readonly SortPreset[] = ["edited", "title", "kind"];

export const LibraryView = ({
    model,
    send,
    pool,
    visibleKinds,
    query,
    openedId,
    renderItem,
    onRowIntent,
    smartFilters = [],
    onAddSource,
    onCreate,
    uploading = [],
    poolState = "loaded",
    returnTo,
}: LibraryViewProps): ReactNode => {
    const { t, i18n } = useLingui();
    const labels = useLibraryLabels();
    // View-ephemeral: the row whose title is being edited inline.
    const [renamingId, setRenamingId] = useState<string | null>(null);

    const view = projectLibrary(model, pool, visibleKinds, query, openedId);
    const { ranked, scope, scopes, scopeCounts, order, markedIndex, opened } =
        view;
    const marked = order[markedIndex]?.id ?? null;

    // Focus follows the author in and out (WCAG 2.4.3): into an opened item
    // its heading takes focus (the item's own h1 once it has rendered, the
    // item region until then); back on the table, the row gone into does.
    // A step with prev/next keeps focus on the stepper. View-ephemeral DOM
    // focus only.
    const itemRef = useRef<HTMLElement>(null);
    const focusedFor = useRef<string | null>(null);
    const lastOpened = useRef<string | null>(null);
    useEffect(() => {
        const section = itemRef.current;
        if (opened === null || section === null) {
            const back = lastOpened.current;
            lastOpened.current = null;
            focusedFor.current = null;
            if (back !== null) document.getElementById(rowId(back))?.focus();
            return;
        }
        lastOpened.current = opened.id;
        const active = document.activeElement;
        const fresh = focusedFor.current !== opened.id;
        if (!fresh && active !== section) return;
        focusedFor.current = opened.id;
        if (fresh && active?.closest(".bsx-bar") != null) return;
        const heading = section.querySelector<HTMLElement>(".bsx-item-body h1");
        if (heading === null) {
            section.focus();
            return;
        }
        heading.setAttribute("tabindex", "-1");
        heading.focus();
    });

    // The marked row in view: back on the table (a remount, a deep link
    // left), and in the table beside an opened item (prev/next walk it).
    useEffect(() => {
        if (marked === null) return;
        document
            .getElementById(rowId(marked))
            ?.scrollIntoView({ block: "nearest" });
    }, [marked]);

    // === Intents =============================================================
    const setQuery = (next: string): void =>
        send({ type: "query_edited", query: next });
    const goIn = (result: SearchResult, page?: number): void => {
        const target = page === undefined ? undefined : pageFocusTarget(page);
        send({
            type: "row_entered",
            id: result.id,
            ...(target !== undefined ? { target } : {}),
        });
    };
    // ↑ ↓ move the mark, Enter goes in — keys of the field's own popup (a
    // combobox, while no suggestion is open), never a chrome chord.
    const onListKey = (key: ListKey): boolean => {
        if (order.length === 0) return false;
        if (key === "enter") {
            const row = order[markedIndex];
            if (row === undefined) return false;
            goIn(row);
            return true;
        }
        const next =
            markedIndex < 0
                ? 0
                : key === "down"
                  ? Math.min(markedIndex + 1, order.length - 1)
                  : Math.max(markedIndex - 1, 0);
        const row = order[next];
        if (row !== undefined) send({ type: "row_marked", id: row.id });
        return true;
    };
    const onRowKey = (key: RowKey): void => {
        onListKey(key);
    };

    // Roving-tabindex keyboard nav over the tab strip (F77). A modified arrow
    // falls through to the browser (F81: never shadow a browser chord).
    const onTabKeyDown = (
        e: KeyboardEvent<HTMLButtonElement>,
        index: number,
    ): void => {
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        const last = scopes.length - 1;
        const next =
            e.key === "ArrowRight"
                ? Math.min(index + 1, last)
                : e.key === "ArrowLeft"
                  ? Math.max(index - 1, 0)
                  : e.key === "Home"
                    ? 0
                    : e.key === "End"
                      ? last
                      : undefined;
        const target = next === undefined ? undefined : scopes[next];
        if (target === undefined) return;
        e.preventDefault();
        setQuery(withScope(query, target));
        // Focus moves with the selection (the tab list's own keys).
        const tabs =
            e.currentTarget.parentElement?.querySelectorAll<HTMLElement>(
                '[role="tab"]',
            );
        if (next !== undefined) tabs?.item(next)?.focus();
    };

    // === Localized words that need this view's data ==========================
    const groupHeading = (key: string, count: number): string => {
        const n = formatCount(count, i18n.locale);
        return key === "sources"
            ? t`Sources · ${n}`
            : key === "title"
              ? t`Title · ${n}`
              : key === "author"
                ? t`Author · ${n}`
                : count > 0
                  ? t`In the content · ${n}`
                  : t`In the content`;
    };
    const contentNote = (section: ContentSection): string | undefined => {
        switch (section.type) {
            case "hidden":
                return undefined;
            case "loading":
                return t`Searching inside the materials…`;
            case "failed":
                return t`Couldn't search inside the content. Try again.`;
            case "empty":
                return t`No matches inside the materials.`;
            case "rows":
                return section.truncated
                    ? t`Showing the first matches. Type more to narrow.`
                    : undefined;
        }
    };

    // The row's trailing actions: an archived row's Unarchive, then the
    // lifecycle kebab projected from the row's state.
    const rowTrailing = (result: SearchResult): ReactNode => {
        const ref = refOfResult(result);
        if (onRowIntent === undefined || ref === null) return undefined;
        // While renaming, the field owns the row (as in the rail).
        if (result.id === renamingId) return undefined;
        const items: readonly MenuItem[] = rowActionsFor(result).map(
            (action): MenuItem => ({
                _tag: "item",
                label: labels.action(action),
                tone: action === "delete" ? "danger" : "default",
                onSelect: () =>
                    action === "rename"
                        ? setRenamingId(result.id)
                        : onRowIntent(intentOfAction(action, ref)),
            }),
        );
        const title = result.title;
        return (
            <>
                {result.archived === true ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                            onRowIntent(intentOfAction("unarchive", ref))
                        }
                    >
                        {t`Unarchive`}
                    </Button>
                ) : null}
                {/* One tab stop per row: only the marked row's kebab is
                tabbable; the others are reached by marking their row. */}
                <RowOverflow
                    label={t`Actions for ${title}`}
                    items={items}
                    tabIndex={result.id === marked ? 0 : -1}
                />
            </>
        );
    };
    const rowRenameField = (result: SearchResult): ReactNode => {
        const ref = refOfResult(result);
        if (onRowIntent === undefined || ref === null) return undefined;
        if (result.id !== renamingId) return undefined;
        const title = result.title;
        return (
            <RowRename
                initial={title}
                label={t`New name for ${title}`}
                onSubmit={(next) => {
                    setRenamingId(null);
                    onRowIntent({ type: "rename-resource", ref, title: next });
                }}
                onCancel={() => setRenamingId(null)}
            />
        );
    };

    const isEmpty =
        order.length === 0 &&
        (view.content.type === "hidden" || view.content.type === "empty");

    // The kind tabs write the query; the text selects the tab. The header's
    // nav row, touching the table. Shown only when they tell rows apart: with
    // one kind and nothing archived, "All" and that kind are the same list
    // (C-01 has no tab row), unless a tab other than "All" is active.
    const tabsSaySomething =
        scopes.length > 2 || (scope !== null && scope !== "all");
    const onlyMaterials = scopes.every((s) => s === "all" || s === "material");
    const libraryTabs = (
        <div
            className="bsx-facets"
            role="tablist"
            aria-label={t`Filter by type`}
        >
            {scopes.map((s, index) => {
                const active = s === scope;
                return (
                    <button
                        key={s}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        aria-controls="bsx-panel"
                        tabIndex={
                            active || (scope === null && index === 0) ? 0 : -1
                        }
                        className={`bsx-facet${active ? " bsx-facet--active" : ""}${s === "archived" ? " bsx-facet--archived" : ""}`}
                        onClick={() => setQuery(withScope(query, s))}
                        onKeyDown={(e) => onTabKeyDown(e, index)}
                    >
                        <span className="bsx-facet-label">
                            {labels.scope(s)}
                        </span>
                        <span className="bsx-facet-count">
                            {formatCount(scopeCounts.get(s) ?? 0, i18n.locale)}
                        </span>
                    </button>
                );
            })}
        </div>
    );

    const sortLabel = (preset: SortPreset): string => {
        switch (preset) {
            case "edited":
                return t`Last edited`;
            case "title":
                return t`Title (A–Z)`;
            case "kind":
                return t`Type`;
            default:
                return exhaustSwitchCase(preset);
        }
    };
    const currentPreset = SORT_PRESET_ORDER.find(
        (p) =>
            SORT_PRESETS[p].column === model.sort.column &&
            SORT_PRESETS[p].direction === model.sort.direction,
    );
    const sortMenu = (
        <Menu
            align="end"
            trigger={
                <Button variant="secondary" size="sm" className="bsx-sort-menu">
                    {currentPreset !== undefined
                        ? sortLabel(currentPreset)
                        : labels.column(model.sort.column)}
                    <Icon name="chevron-down" size="sm" />
                </Button>
            }
            items={SORT_PRESET_ORDER.map(
                (preset): MenuItem => ({
                    _tag: "item",
                    label: sortLabel(preset),
                    icon:
                        preset === currentPreset ? (
                            <Icon name="check" size="xs" />
                        ) : undefined,
                    onSelect: () =>
                        send({
                            type: "sort_chosen",
                            sort: SORT_PRESETS[preset],
                        }),
                }),
            )}
        />
    );

    return (
        <div
            className={`bsx-centre${opened !== null ? " bsx-centre--open" : ""}`}
        >
            <ViewColumn
                className={`bsx-index${opened !== null ? " bsx-index--behind" : ""}`}
            >
                <ViewHeader
                    title={t`Library`}
                    tools={
                        <>
                            <CreateCard
                                onCreate={onCreate}
                                onAddSource={onAddSource}
                            />
                            <QueryField
                                query={query}
                                onQueryChange={setQuery}
                                tokens={view.tokens}
                                chipKeys={CHIP_KEYS}
                                pool={pool}
                                activeOptionId={
                                    marked === null ? undefined : rowId(marked)
                                }
                                expanded={order.length > 0}
                                onListKey={onListKey}
                            />
                            {poolState === "waiting" ? null : (
                                <div className="bsx-toolbar">
                                    <Text
                                        as="span"
                                        size="sm"
                                        tone="dim"
                                        className="bsx-count"
                                    >
                                        {/* C-01: "7 materials" while
                                        browsing; a narrowed list says how
                                        many it shows. */}
                                        {query.trim() === "" && onlyMaterials
                                            ? t`${plural(order.length, { one: "# material", other: "# materials" })}`
                                            : t({
                                                  message: `${formatCount(order.length, i18n.locale)} shown`,
                                                  context: "library count",
                                              })}
                                    </Text>
                                    {/* The saved searches: next to the field they fill. */}
                                    <SmartFilterBar
                                        view={projectSmartFilters(
                                            model,
                                            smartFilters,
                                            query,
                                        )}
                                        query={query}
                                        failure={model.smartFilterFailure}
                                        send={send}
                                    />
                                    {sortMenu}
                                </div>
                            )}
                        </>
                    }
                    nav={
                        poolState === "waiting" || !tabsSaySomething
                            ? undefined
                            : libraryTabs
                    }
                />

                {/* A filter that does nothing says so (a live region, always
                mounted so the note is announced when it appears). */}
                <div role="status" className="bsx-status">
                    {view.hint !== undefined ? (
                        <Text
                            as="p"
                            size="xs"
                            tone="dim"
                            className="bsx-content-note"
                        >
                            {labels.hint(view.hint)}
                        </Text>
                    ) : null}
                    {uploading.map((filename, i) => (
                        <Text
                            // biome-ignore lint/suspicious/noArrayIndexKey: two queued files may share a name; the batch order is stable
                            key={i}
                            as="p"
                            size="xs"
                            tone="dim"
                            className="bsx-content-note"
                        >
                            {t`Uploading ${filename}…`}
                        </Text>
                    ))}
                </div>

                {ranked.correction !== undefined ? (
                    <Text
                        as="p"
                        size="xs"
                        tone="dim"
                        className="bsx-content-note"
                    >
                        {t`No exact matches for "${ranked.correction.typed}". Showing "${ranked.correction.matched}".`}
                    </Text>
                ) : null}

                <div id="bsx-panel" role="tabpanel" className="bsx-panel">
                    {poolState === "waiting" ? (
                        <EmptyState
                            icon={<Icon name="wifi-off" size="lg" />}
                            title={t`Can't show the library right now`}
                            body={t`It appears as soon as the connection to the server is back.`}
                        />
                    ) : isEmpty ? (
                        <EmptyState
                            icon={<Icon name="search" size="lg" />}
                            title={
                                query.trim() !== ""
                                    ? t`No matches for "${query}"`
                                    : t`Nothing here yet`
                            }
                            body={
                                view.archivedMatches > 0
                                    ? t`Nothing among the active items, but the archive has matches.`
                                    : query.trim() !== ""
                                      ? t`Try another word, or widen the scope above.`
                                      : t`Materials show up here as you create them. Sources are in the knowledge bank.`
                            }
                        >
                            {view.archivedMatches > 0 ? (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() =>
                                        setQuery(withScope(query, "archived"))
                                    }
                                >
                                    {t`Show archived (${view.archivedMatches})`}
                                </Button>
                            ) : null}
                        </EmptyState>
                    ) : (
                        <div className="bsx-results">
                            <LibraryTable
                                ranked={ranked}
                                content={view.content}
                                sort={model.sort}
                                labels={labels}
                                groupHeading={groupHeading}
                                contentNote={contentNote}
                                markedId={marked}
                                openedId={openedId}
                                rowId={rowId}
                                onOpen={goIn}
                                onRowKey={onRowKey}
                                trailing={rowTrailing}
                                renameField={rowRenameField}
                                pageLabel={(page) => t`Page ${page}`}
                                ariaLabel={t`Library`}
                            />
                        </div>
                    )}
                </div>
            </ViewColumn>

            {opened !== null ? (
                // Scrim-like dismissal (D37, D39): one pointer target over the
                // whole centre behind the sheet, mapping to the same msg as
                // the ×. Keyboard users keep × and Back.
                <div
                    className="bsx-index-dismiss"
                    aria-hidden="true"
                    onClick={() => send({ type: "list_returned" })}
                />
            ) : null}

            {opened !== null ? (
                <section
                    ref={itemRef}
                    tabIndex={-1}
                    className="bsx-item"
                    aria-label={opened.title}
                    onKeyDown={(e) => {
                        if (returnTo === undefined) return;
                        if (!isLeaveKey(leaveKeyOf(e))) return;
                        e.preventDefault();
                        returnTo.onReturn();
                    }}
                >
                    <DrillInBar
                        {...(returnTo === undefined
                            ? {}
                            : {
                                  back: {
                                      label: returnTo.label,
                                      onBack: returnTo.onReturn,
                                  },
                              })}
                        {...(opened.archived === true
                            ? { note: labels.archived(opened.kind) }
                            : {})}
                        position={view.position}
                        onClose={() => send({ type: "list_returned" })}
                        onStep={(to) => send({ type: "stepped", id: to.id })}
                    />
                    <div className="bsx-item-body">{renderItem(opened)}</div>
                </section>
            ) : null}
        </div>
    );
};
